import {Controller} from "stimulus"
import {DateTime, Duration} from "luxon";
import StimulusReflex from 'stimulus_reflex'

const SHOW_VIDEO_CLASS = 'show-video';
const CONFIRM_PLAY_CLASS = 'show';
const EXTRA_TIME_AFTER = 3612; // it should be video time seconds stamp when Cleo finish dialogue
const VIDEO_DELAY_SECS = 2; // it looks like video has extra 2 seconds, so try synchronize with countdown

function convertTimestampToSeconds(timestamp) {
    const [minutes, seconds] = timestamp.split(":");
    const secondsTimestamp = parseInt(minutes, 10) * 60 + parseInt(seconds, 10);
    return secondsTimestamp;
}


const baronBarks = [["03:32", "03:37"],
["12:12", "12:18"],
["12:22", "12:25"],
["17:20", "17:29"],
["20:04", "20:10"],
["22:52", "22:59"],
["32:54", "33:01"],
["53:52", "53:58"]]
const baronBarksSeconds = baronBarks.map((c)=>{return [convertTimestampToSeconds(c[0]),convertTimestampToSeconds(c[1])]})
const cleoBarks= [["08:02", "08:05"], 
["12:18", "12:22"], 
["15:13", "15:15"], 
["25:44", "25:46"], 
["28:23", "28:27"], 
["36:35", "36:38"], 
["40:48", "40.53"], 
["43:42", "43:44"], 
["51:04", "51:07"], 
["59:54", "60:08"], 
["80:00", "80:16"]]
const cleoBarksSeconds = cleoBarks.map((c)=>{return [convertTimestampToSeconds(c[0]),convertTimestampToSeconds(c[1])]})

export default class extends Controller {
    static targets = ["background", "confirmPlay", "video", "minutes", "seconds", "baron","cleo", "fuel"];

    videoIsShowing = false;
    timerInterval = null;
    videoSecs = 0;

    clearTimerInterval = () => {
        clearInterval(this.timerInterval);
    };

    toggleVideo(show = false) {
        if (this.hasBackgroundTarget && this.hasVideoTarget) {
            if (show) {
                this.play_video()
            } else {
                this.stop_video();
            }
        }
    }

    connect() {
        StimulusReflex.register(this);
        this.startTimer();
        if (this.hasVideoTarget) {
            this.videoTarget.addEventListener('loadedmetadata', (event) => {
                const video = event.target;
                if (video) {
                    const currentTime = parseInt(video.dataset['time']);
                    if (currentTime < video.duration) {
                        if (!Number.isNaN(currentTime) && currentTime !== 0) {
                            video.currentTime = currentTime + VIDEO_DELAY_SECS;
                        }
                        video.muted = video.dataset['muted'] === 'true'; // to make possible play without first user interaction
                    }
                }
            }, false);
            // just attempt to prevent playing video on a background
            this.videoTarget.addEventListener('DOMNodeRemoved', (event) => (event.target.pause()), false);
        }
    }

    disconnect() {
        this.clearTimerInterval();
    }

    startTimer() {
        this.timerInterval = setInterval(() => {
            this.tick()
        }, 1000)
    }

    tick() {
        const start = DateTime.fromISO(this.data.get("gameStartedAt")).toUTC();
        const timeLimitSecs = parseInt(this.data.get("timeLimitSecs")) || 0;
        const timePenaltySecs = parseInt(this.data.get("timePenaltySecs")) || 0;
        const timeExtraSecs = parseInt(this.data.get("timeExtraSecs")) || 0;
        const gameWithExtraTime = Boolean(timeExtraSecs);
        const now = DateTime.local().toUTC();
        const end = start.plus({seconds: timeLimitSecs - timePenaltySecs + timeExtraSecs});



        const {seconds: gameGone} = now.diff(start.minus({seconds: timePenaltySecs}), 'seconds').toObject();
        const secondsGone = Math.round(gameGone);

        this.showCharacterBarks(secondsGone);

        if (secondsGone >= EXTRA_TIME_AFTER && !gameWithExtraTime) {
            this.stimulate('Game#add_extra_time');
        }
        this.updateVideo(secondsGone);
        this.updateTimeDisplay(end.diff(now).toObject(), gameWithExtraTime);

        if (this.data.get("refreshAt") && new Date(this.data.get("refreshAt")) < new Date()) {
            this.stimulate();
        }
    }

    showCharacterBarks(secondsGone) {
        this.baronTarget.classList.remove('show');
        for (const [start, end] of baronBarksSeconds) {
            if (secondsGone >= start && secondsGone <= end) {
                this.baronTarget.classList.add('show');
            }
        }
        this.cleoTarget.classList.remove('show');
        for (const [start, end] of cleoBarksSeconds) {
            if (secondsGone >= start && secondsGone <= end) {
                this.cleoTarget.classList.add('show');
            }
        }
    }



    updateVideo(secondsGone) {
        if (this.hasVideoTarget) {
            const currentTime = Math.round(this.videoTarget.currentTime + VIDEO_DELAY_SECS);
            const duration = Math.round(this.videoTarget.duration);

            if (secondsGone < duration) {
                // ensure video is muted sounds
                this.videoTarget.muted = this.data.get("soundsMuted") === 'true';

                // ensure video delay less than 3 seconds
                if (Math.abs(secondsGone - currentTime) >= 3) {
                    this.videoTarget.currentTime = secondsGone;
                }

                // turn on video
                if (!this.videoIsShowing) {
                    this.toggleVideo(true);
                }
            } else {
                // turn off video
                if (this.videoIsShowing) {
                    this.toggleVideo(false);
                }
            }
        }
    }

    updateTimeDisplay(diff = {}, gameWithExtraTime = false) {
        if (this.hasMinutesTarget && this.hasSecondsTarget) {
            const {milliseconds} = diff;
            if (milliseconds > 1000) {
                const remaining = Duration.fromObject(diff).toFormat('mm:ss');
                const [minutes, seconds] = remaining.split(':');
                this.minutesTarget.innerHTML = minutes;
                this.secondsTarget.innerHTML = seconds;
            } else if (!gameWithExtraTime) {
                // pause countdown on last second before add extra time
                this.minutesTarget.innerHTML = '00';
                this.secondsTarget.innerHTML = '01';
            } else {
                this.minutesTarget.innerHTML = '00';
                this.secondsTarget.innerHTML = '00';
            }
        }
    }


    play_video() {
        if (this.hasVideoTarget) {
            this.videoTarget.play()
                .then(() => {
                    this.backgroundTarget.classList.add(SHOW_VIDEO_CLASS);
                    this.videoIsShowing = true;
                    this.confirmPlayTarget.classList.remove(CONFIRM_PLAY_CLASS);
                })
                .catch((err) => {
                    this.ask_to_play();
                    console.log('err', err)
                });
        }
    }

    stop_video() {
        this.videoIsShowing = false;
        this.backgroundTarget.classList.remove(SHOW_VIDEO_CLASS);
    }

    ask_to_play() {
        this.confirmPlayTarget.classList.add(CONFIRM_PLAY_CLASS);
    }

    pause_video() {
        const video = this.videoTarget;
        if (video.currentTime !== video.duration) {
            this.ask_to_play();
        }
    }
}
